import { Editor } from 'grapesjs';
import { fetchAssets, getFileType, getFileName, uploadAsset, deleteAsset } from './api/assets.utils';
import { WebsiteType } from '../types/website.types';

export const createCustomVideoComponent = (editor: Editor, websiteType: WebsiteType) => {
  editor.Components.addType('custom-video', {
    model: {
      defaults: {
        tagName: 'video',
        attributes: {
          src: '',
          controls: true,
          style:
            'max-width: 100%; height: auto; min-height: 200px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; border: 2px dashed #d1d5db;',
          title: 'Click to select video',
        },
        stylable: [
          'width',
          'min-width',
          'max-width',
          'height',
          'min-height',
          'max-height',
          'margin',
          'margin-top',
          'margin-right',
          'margin-bottom',
          'margin-left',
          'padding',
          'padding-top',
          'padding-right',
          'padding-bottom',
          'padding-left',
          'align-items',
          'justify-content',
          'object-fit',
          'background',
          'background-color',
          'border',
          'border-width',
          'border-style',
          'border-color',
          'border-radius',
          'box-shadow',
          'opacity',
        ],
        resizable: {
          tl: 1,
          tc: 1,
          tr: 1,
          cl: 1,
          cr: 1,
          bl: 1,
          bc: 1,
          br: 1,
        },
        traits: [
          {
            type: 'text',
            name: 'src',
            label: 'Video URL',
            placeholder: 'Enter video URL or use asset selector',
          },
          {
            type: 'checkbox',
            name: 'controls',
            label: 'Show Controls',
          },
          {
            type: 'checkbox',
            name: 'autoplay',
            label: 'Autoplay',
          },
          {
            type: 'checkbox',
            name: 'muted',
            label: 'Muted',
          },
          {
            type: 'checkbox',
            name: 'loop',
            label: 'Loop',
          },
        ],
      },
    },
    view: {
      events() {
        return {
          dblclick: 'openVideoSelector',
        };
      },

      onRender() {
        const attrs = this.model.getAttributes();
        const { src, controls, autoplay, muted, loop, ...otherAttrs } = attrs;

        // Build video attributes
        const videoAttrs = [];

        if (src) videoAttrs.push(`src="${src}"`);

        // Always add controls by default unless explicitly set to false
        if (controls !== false) {
          videoAttrs.push('controls');
        }

        if (autoplay) videoAttrs.push('autoplay');
        if (muted) videoAttrs.push('muted');
        if (loop) videoAttrs.push('loop');

        // Add other attributes (including style)
        Object.entries(otherAttrs).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            videoAttrs.push(`${key}="${value}"`);
          }
        });

        const videoAttrString = videoAttrs.join(' ');

        if (src) {
          this.el.innerHTML = `<video ${videoAttrString}><source src="${src}" type="video/mp4">Your browser does not support the video tag.</video>`;
        } else {
          this.el.innerHTML = `<video ${videoAttrString}>Your browser does not support the video tag.</video>`;
        }
      },
      openVideoSelector() {
        this.openVideoAssetSelector();
      },

      async openVideoAssetSelector() {
        const editor = this.model.em;
        const modal = editor.Modal;

        // Show loading state
        modal.setTitle('Select Video');
        modal.setContent(createLoadingState());
        modal.open();

        try {
          const assetUrls = await fetchAssets(websiteType);
          const videoAssets = assetUrls
            .filter((asset: { id: string; url: string }) => getFileType(asset.url) === 'video')
            .map(asset => ({
              id: asset.id,
              name: getFileName(asset.url),
              type: getFileType(asset.url),
              src: asset.url,
            }));

          // Create modal content
          const container = document.createElement('div');
          container.style.cssText = `
            padding: 0;
            max-height: 600px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          `;

          // Create header with upload button
          const header = document.createElement('div');
          header.style.cssText = `
            display: flex;
            justify-content: flex-end;
            width: 100%;
          `;
          header.innerHTML = `
            <button 
              id="upload-video-btn"
              class="primary-button"
              style="padding: 7px 26px; font-size: 15px;"
            >
              Upload
            </button>
          `;
          container.appendChild(header);

          // Create hidden file input
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'video/*';
          fileInput.style.display = 'none';
          fileInput.multiple = false;
          container.appendChild(fileInput);

          // Create scrollable content
          const content = document.createElement('div');
          content.style.cssText = `
            padding: 24px;
            overflow-y: auto;
            flex: 1;
          `;

          if (videoAssets.length === 0) {
            content.innerHTML = `
              <div style="padding: 40px; text-align: center; color: #6b7280;">
                <div style="margin-bottom: 16px;">
                  <svg style="width: 48px; height: 48px; color: #d1d5db; margin: 0 auto; display: block;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </div>
                <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 18px;">No videos found</h3>
                <p style="margin: 0; color: #6b7280;">Upload your first video to get started.</p>
              </div>
            `;
          } else {
            // Create grid
            const grid = document.createElement('div');
            grid.style.cssText = `
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
              gap: 16px;
            `;

            videoAssets.forEach(asset => {
              const card = document.createElement('div');
              card.style.cssText = `
                background: white;
                border: 1px solid #e5e7eb;
                border-radius: 12px;
                overflow: hidden;
                cursor: pointer;
                transition: all 0.2s ease;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              `;

              card.innerHTML = `
                <div style="position: relative; background: #f3f4f6; aspect-ratio: 16/9;">
                  <video 
                    style="width: 100%; height: 100%; object-fit: cover;"
                    preload="metadata"
                    muted
                    loop
                    playsinline
                  >
                    <source src="${asset.src}" type="video/mp4">
                  </video>
                  <div style="
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: rgba(0, 0, 0, 0.8);
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 16px;
                    transition: all 0.2s ease;
                  ">
                    ▶
                  </div>
                  <button class="delete-btn" style="
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background: rgba(220, 38, 38, 0.9);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 18px;
                    font-weight: bold;
                    line-height: 1;
                    padding: 0;
                    margin: 0;
                    transition: all 0.2s ease;
                    opacity: 0;
                    transform: scale(0.8);
                  " onmouseover="this.style.background='rgba(185, 28, 28, 0.9)'" onmouseout="this.style.background='rgba(220, 38, 38, 0.9)'">
                    <span style="display: block; transform: translateY(-1px);">×</span>
                  </button>
                </div>
                <div style="padding: 16px;">
                  <div style="
                    font-weight: 500;
                    color: #111827;
                    font-size: 14px;
                    margin-bottom: 4px;
                    word-break: break-word;
                    line-height: 1.4;
                  ">
                    ${asset.name}
                  </div>
                  <div style="
                    color: #6b7280;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                  ">
                    ${asset.type}
                  </div>
                </div>
              `;

              // Hover effects
              card.addEventListener('mouseenter', () => {
                card.style.borderColor = '#3b82f6';
                card.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                card.style.transform = 'translateY(-2px)';

                // Show delete button
                const deleteBtn = card.querySelector('.delete-btn') as HTMLElement;
                if (deleteBtn) {
                  deleteBtn.style.opacity = '1';
                  deleteBtn.style.transform = 'scale(1)';
                }

                // Play video on hover
                const video = card.querySelector('video');
                if (video) {
                  video.play().catch(() => {});
                }
              });

              card.addEventListener('mouseleave', () => {
                card.style.borderColor = '#e5e7eb';
                card.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                card.style.transform = 'translateY(0)';

                // Hide delete button
                const deleteBtn = card.querySelector('.delete-btn') as HTMLElement;
                if (deleteBtn) {
                  deleteBtn.style.opacity = '0';
                  deleteBtn.style.transform = 'scale(0.8)';
                }

                // Pause video on mouse leave
                const video = card.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              });

              // Click to select
              card.addEventListener('click', () => {
                const currentAttrs = this.model.getAttributes();
                this.model.setAttributes({
                  ...currentAttrs,
                  src: asset.src,
                });
                this.render();
                modal.close();
              });

              // Delete button click handler
              const deleteBtn = card.querySelector('.delete-btn') as HTMLElement;
              if (deleteBtn) {
                deleteBtn.addEventListener('click', async e => {
                  e.stopPropagation(); // Prevent card selection

                  // Show confirmation dialog
                  const confirmed = window.confirm(
                    `Are you sure you want to delete "${asset.name}"? This action cannot be undone.`
                  );
                  if (!confirmed) return;

                  try {
                    // Show loading state on the card
                    card.style.opacity = '0.5';
                    card.style.pointerEvents = 'none';

                    // Delete the asset
                    await deleteAsset(asset.id, websiteType);

                    // Remove the card from the grid
                    card.remove();

                    // If no more assets, show empty state
                    const remainingCards = grid.querySelectorAll('div[style*="background: white"]');
                    if (remainingCards.length === 0) {
                      content.innerHTML = `
                        <div style="padding: 40px; text-align: center; color: #6b7280;">
                          <div style="margin-bottom: 16px;">
                            <svg style="width: 48px; height: 48px; color: #d1d5db; margin: 0 auto; display: block;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                              <line x1="8" y1="21" x2="16" y2="21"/>
                              <line x1="12" y1="17" x2="12" y2="21"/>
                            </svg>
                          </div>
                          <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 18px;">No videos found</h3>
                          <p style="margin: 0; color: #6b7280;">Upload your first video to get started.</p>
                        </div>
                      `;
                    }
                  } catch (error) {
                    console.error('Error deleting video:', error);
                    // Restore card state
                    card.style.opacity = '1';
                    card.style.pointerEvents = 'auto';

                    // Show error message
                    window.alert('Failed to delete video. Please try again.');
                  }
                });
              }

              grid.appendChild(card);
            });

            content.appendChild(grid);
          }

          container.appendChild(content);

          // Add upload functionality
          const uploadBtn = container.querySelector('#upload-video-btn');
          if (uploadBtn) {
            uploadBtn.addEventListener('click', () => {
              fileInput.click();
            });
          }

          // Handle file upload
          fileInput.addEventListener('change', async event => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (!file) return;

            // Validate file type
            if (!file.type.startsWith('video/')) {
              window.alert('Please select a valid video file.');
              return;
            }

            const uploadBtn = container.querySelector('#upload-video-btn') as HTMLElement;

            if (uploadBtn) {
              uploadBtn.style.display = 'none';
            }

            content.innerHTML = `
              <div style="padding: 40px; text-align: center;">
                <div style="margin-bottom: 16px;">
                  <div style="width: 48px; height: 48px; border: 3px solid #e5e7eb; border-top: 3px solid rgb(46, 49, 52); border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
                </div>
                <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 18px;">Uploading video...</h3>
                <p style="margin: 0; color: #6b7280;">Please wait while we upload your video.</p>
                <style>
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                </style>
              </div>
            `;

            try {
              // Upload the file
              const uploadedAsset = await uploadAsset(file, websiteType);

              // Update the video component with the new asset
              const currentAttrs = this.model.getAttributes();
              this.model.setAttributes({
                ...currentAttrs,
                src: uploadedAsset.url,
              });
              this.render();
              modal.close();
            } catch (error) {
              console.error('Error uploading video:', error);

              if (uploadBtn) {
                uploadBtn.style.display = 'block';
              }

              content.innerHTML = `
                <div style="padding: 40px; text-align: center; color: #dc2626;">
                  <div style="margin-bottom: 16px;">
                    <svg style="width: 48px; height: 48px; color: #dc2626; margin: 0 auto; display: block;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                  </div>
                  <h3 style="margin: 0 0 8px 0; color: #dc2626; font-size: 18px;">Upload failed</h3>
                  <p style="margin: 0; color: #6b7280;">There was an error uploading your video. Please try again.</p>
                  <button onclick="location.reload()" style="
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 16px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    margin-top: 16px;
                  ">Try Again</button>
                </div>
              `;
            }
          });

          // Add drag and drop functionality
          container.addEventListener('dragover', e => {
            e.preventDefault();
            container.style.background = '#f0f9ff';
            container.style.border = '2px dashed #3b82f6';
          });

          container.addEventListener('dragleave', e => {
            e.preventDefault();
            container.style.background = '';
            container.style.border = '';
          });

          container.addEventListener('drop', e => {
            e.preventDefault();
            container.style.background = '';
            container.style.border = '';

            const files = e.dataTransfer?.files;
            if (files && files.length > 0) {
              const file = files[0];
              if (file.type.startsWith('video/')) {
                // Simulate file input change
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                fileInput.files = dataTransfer.files;
                fileInput.dispatchEvent(new Event('change', { bubbles: true }));
              } else {
                window.alert('Please drop a valid video file.');
              }
            }
          });

          // Update modal content
          modal.setContent(container);
        } catch (error) {
          console.error('Error loading video assets:', error);
          modal.setContent('Error loading videos. Please try again or check your connection.');
        }
      },
    },
  });
};

// Add custom blocks for the video component
export const addCustomVideoBlocks = (editor: Editor) => {
  const blockManager = editor.BlockManager;

  blockManager.add('custom-video', {
    label: 'Video',
    category: 'Media',
    content: {
      type: 'custom-video',
      attributes: {
        src: '',
        controls: true,
        style:
          'max-width: 100%; height: auto; min-height: 200px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; border: 2px dashed #d1d5db;',
      },
    },
    media: `<svg viewBox="0 0 24 24" style="width: 24px; height: 24px;"><path fill="currentColor" d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/></svg>`,
  });
};

function createLoadingState(): HTMLElement {
  // Create loading container using DOM
  const loadingContainer = document.createElement('div');
  loadingContainer.style.cssText = `
    padding: 40px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  `;

  // Create spinner
  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 32px;
    height: 32px;
    animation: spin 1s linear infinite;
  `;

  const spinnerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  spinnerSvg.setAttribute('viewBox', '0 0 24 24');
  spinnerSvg.setAttribute('fill', 'none');
  spinnerSvg.setAttribute('stroke', 'currentColor');
  spinnerSvg.setAttribute('stroke-width', '2');

  const spinnerPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  spinnerPath.setAttribute('d', 'M21 12a9 9 0 11-6.219-8.56');
  spinnerSvg.appendChild(spinnerPath);
  spinner.appendChild(spinnerSvg);

  // Create loading text
  const loadingText = document.createElement('div');
  loadingText.textContent = 'Loading videos...';
  loadingText.style.cssText = `
    color: #6b7280;
    font-size: 16px;
  `;

  loadingContainer.appendChild(spinner);
  loadingContainer.appendChild(loadingText);

  // Add CSS animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  loadingContainer.appendChild(style);

  return loadingContainer;
}
